import {ofType} from 'redux-observable';
import {of, from, EMPTY} from 'rxjs';
import {mergeMap, map, catchError} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as JoinMeetingModalActions from 'web-app/react/modals-container/join-famly-live-meeting-modal/actions';
import * as DraftActions from 'web-app/react/feed/drafts/actions';

import MeetingManager from './meeting-manager';
import * as Actions from './actions';
import * as API from './api';
import * as Constants from './constants';

const createMeeting = action$ =>
    action$.pipe(
        ofType(Actions.createMeeting.type),
        mergeMap(({payload}: ReturnType<typeof Actions.createMeeting.action>) =>
            API.createMeeting().pipe(
                map(({famlyMeeting}) => {
                    payload.payload.addFamlyLiveMeetingToPost(famlyMeeting.meeting.famlyMeetingId);
                    return Actions.createMeetingSuccess.action(famlyMeeting);
                }),
                catchError(e => of(Actions.createMeetingFailed.action(e))),
            ),
        ),
    );

const meetingPosted = action$ =>
    action$.pipe(
        ofType(DraftActions.sendSuccess.type),
        mergeMap(({payload}: ReturnType<typeof DraftActions.sendSuccess.action>) => {
            const {famlyLiveMeetingId} = payload;
            if (famlyLiveMeetingId) {
                return of(JoinMeetingModalActions.show.action(payload.famlyLiveMeetingId, true));
            } else {
                return EMPTY;
            }
        }),
    );

const joinMeeting = action$ =>
    action$.pipe(
        ofType(Actions.joinMeeting.type),
        mergeMap(({payload}: ReturnType<typeof Actions.joinMeeting.action>) =>
            from(MeetingManager().then(manager => manager.joinMeeting(payload.meetingId, payload.joinToken))).pipe(
                map(() => Actions.joinMeetingSuccess.action(payload.meetingId)),
                catchError(e => of(Actions.joinMeetingFailed.action(e))),
            ),
        ),
    );

const startLocalVideo = action$ =>
    action$.pipe(
        ofType(Actions.startLocalVideo.type),
        mergeMap(({payload}: ReturnType<typeof Actions.startLocalVideo.action>) => {
            return from(
                MeetingManager().then(manager => manager.startLocalVideo(payload.videoDevice?.deviceId ?? null)),
            ).pipe(map(() => Actions.startLocalVideoSuccess.action(payload.videoDevice?.deviceId)));
        }),
        catchError(e => of(Actions.startLocalVideoFailed.action(e))),
    );

const stopLocalVideo = action$ =>
    action$.pipe(
        ofType(Actions.stopLocalVideo.type),
        mergeMap(() => {
            return from(MeetingManager().then(manager => manager.stopLocalVideo())).pipe(
                map(() => Actions.stopLocalVideoSuccess.action()),
            );
        }),
        catchError(e => of(Actions.stopLocalVideoFailed.action(e))),
    );

const muteLocalAudio = action$ =>
    action$.pipe(
        ofType(Actions.muteLocalAudio.type),
        mergeMap(() => {
            return from(MeetingManager().then(manager => manager.muteLocalAudio())).pipe(
                map(() => Actions.muteLocalAudioSuccess.action()),
            );
        }),
        catchError(e => of(Actions.muteLocalAudioFailed.action(e))),
    );

const unmuteLocalAudio = action$ =>
    action$.pipe(
        ofType(Actions.unmuteLocalAudio.type),
        mergeMap(() => {
            return from(MeetingManager().then(manager => manager.unmuteLocalAudio())).pipe(
                map(() => Actions.unmuteLocalAudioSuccess.action()),
            );
        }),
        catchError(e => of(Actions.unmuteLocalAudioFailed.action(e))),
    );

const leaveMeeting = action$ =>
    action$.pipe(
        ofType(Actions.leaveMeeting.type),
        mergeMap(() => {
            return from(MeetingManager().then(manager => manager.leaveMeeting())).pipe(
                map(() => Actions.leaveMeetingSuccess.action()),
            );
        }),
    );

const attendeeAdded = (action$, state$) =>
    action$.pipe(
        ofType(Actions.addAttendeeId.type),
        map(() =>
            Actions.getMeeting.action(state$.value.famlyLive.famlyMeetingId, state$.value.famlyLive.refreshToken),
        ),
    );

const getMeeting = action$ =>
    action$.pipe(
        ofType(Actions.getMeeting.type),
        mergeMap(({payload}: ReturnType<typeof Actions.getMeeting.action>) =>
            API.getMeeting(payload.famlyLiveMeetingId, payload.refreshToken).pipe(
                map(({famlyMeeting}) => Actions.getMeetingSuccess.action(famlyMeeting)),
                catchError(e => of(Actions.getMeetingFailed.action(e))),
            ),
        ),
    );

const getJoinToken = action$ =>
    action$.pipe(
        ofType(Actions.getJoinToken.type),
        mergeMap(({payload}: ReturnType<typeof Actions.getJoinToken.action>) =>
            API.getJoinToken(payload.famlyLiveMeetingId).pipe(
                map(({externalJoinUrl}) => Actions.getJoinTokenSuccess.action(externalJoinUrl)),
                catchError(e => of(Actions.getJoinTokenFailed.action(e))),
            ),
        ),
    );

const setSelectedAudioInputDevice = action$ =>
    action$.pipe(
        ofType(Actions.setSelectedAudioInputDevice.type),
        mergeMap(({payload}: ReturnType<typeof Actions.setSelectedAudioInputDevice.action>) => {
            return from(
                MeetingManager().then(manager =>
                    manager.chooseAudioInputDevice(payload.audioInputDevice?.deviceId ?? null),
                ),
            ).pipe(map(() => Actions.setSelectedAudioInputDeviceSuccess.action(payload.audioInputDevice)));
        }),
        catchError(e => of(Actions.setSelectedAudioInputDeviceFailed.action(e))),
    );

const setSelectedAudioOutputDevice = action$ =>
    action$.pipe(
        ofType(Actions.setSelectedAudioOutputDevice.type),
        mergeMap(({payload}: ReturnType<typeof Actions.setSelectedAudioOutputDevice.action>) => {
            return from(
                MeetingManager().then(manager => {
                    manager.chooseAudioOutputDevice(payload.audioOutputDevice?.deviceId ?? null).then(() => {
                        const audioElement = document.getElementById(
                            Constants.MEETING_AUDIO_ID,
                        ) as HTMLAudioElement | null;
                        if (audioElement) {
                            manager.bindAudioElement(audioElement);
                        }
                    });
                }),
            ).pipe(map(() => Actions.setSelectedAudioOutputDeviceSuccess.action(payload.audioOutputDevice)));
        }),
        catchError(e => of(Actions.setSelectedAudioOutputDeviceFailed.action(e))),
    );

const setSelectedVideoInputDevice = action$ =>
    action$.pipe(
        ofType(Actions.setSelectedVideoInputDevice.type),
        mergeMap(({payload}: ReturnType<typeof Actions.setSelectedVideoInputDevice.action>) => {
            return from(
                MeetingManager().then(manager => {
                    return manager.chooseVideoInputDevice(payload.videoInputDevice?.deviceId ?? null);
                }),
            ).pipe(map(() => Actions.setSelectedVideoInputDeviceSuccess.action(payload.videoInputDevice)));
        }),
        catchError(e => of(Actions.setSelectedVideoInputDeviceFailed.action(e))),
    );

export default combineEpics(
    createMeeting,
    joinMeeting,
    startLocalVideo,
    stopLocalVideo,
    muteLocalAudio,
    unmuteLocalAudio,
    leaveMeeting,
    getMeeting,
    getJoinToken,
    attendeeAdded,
    setSelectedAudioInputDevice,
    setSelectedAudioOutputDevice,
    setSelectedVideoInputDevice,
    meetingPosted,
);
